import { AppMode } from "@client/ImperativeState";
import { getMeanRecommendation } from "@engine/round/updateMeanRecommender";
import { meanUserId } from "@lib/constants";
import { FailedLookupById } from "@lib/FailedLookupById";
import { InvalidInputError } from "@lib/InvalidInputError";
import {
  MVFProfile,
  TrancheProfile,
  getTrancheBottoms,
  mvfProfileFromTrancheProfile,
  tranchesFromMVFProfile,
} from "@lib/tranche";
import { Evaluable, Evaluator, Track, trackNames } from "@lib/types";
import {
  Evaluation,
  Funder,
  Node,
  Proposal,
  ProposalForm,
  Recommender,
  RoundAggregate,
  RoundMember,
} from "./aggregate";

export function getEvaluation(
  round: RoundAggregate,
  evaluatorId: string,
  evaluableId: string,
): Evaluation | null {
  const evaluator = getEvaluator(round, evaluatorId);
  if (!evaluator) {
    return null;
  }

  const evaluation = evaluator.evaluations[evaluableId];
  if (!evaluation) {
    return null;
  }

  return evaluation;
}

export function assertGetEvaluation(
  round: RoundAggregate,
  evaluatorId: string,
  evaluableId: string,
): Evaluation {
  const evaluation = getEvaluation(round, evaluatorId, evaluableId);
  if (!evaluation) {
    throw new Error(
      `Failed to find evaluation. evaluatorId: ${evaluatorId}, evaluableId: ${evaluableId}`,
    );
  }

  return evaluation;
}

export function getMemberForEvaluator(
  round: RoundAggregate,
  evaluatorId: string,
): RoundMember | null {
  return (
    Object.values(round.members).find(
      (member) => member.evaluators[evaluatorId],
    ) ?? null
  );
}

export function getEvaluator(
  round: RoundAggregate,
  evaluatorId: string,
): Recommender | Funder | null {
  for (const member of Object.values(round.members)) {
    if (member.evaluators[evaluatorId]) {
      return member.evaluators[evaluatorId];
    }
  }

  return null;
}

export function assertGetEvaluator(
  round: RoundAggregate,
  evaluatorId: string,
): Recommender | Funder {
  const evaluator = getEvaluator(round, evaluatorId);
  if (!evaluator) {
    throw new FailedLookupById("evaluator", round, evaluatorId);
  }

  return evaluator;
}

export function assertGetRecommender(
  round: RoundAggregate,
  recommenderId: string,
): Recommender {
  const recommender = assertGetEvaluator(round, recommenderId);
  if (recommender.type !== "recommender") {
    throw new Error(
      `Found non-recommender evaluator when looking for a recommender, id: ${recommenderId}`,
    );
  }

  return recommender;
}

export function getEvaluableByShortname(
  round: RoundAggregate,
  shortname: string,
): Evaluable | undefined {
  return getAllEvaluables(round).find(
    (evaluable) => evaluable.shortname === shortname,
  );
}

export function getAllEvaluables(round: RoundAggregate): Evaluable[] {
  const recommenders = getAllRecommenders(round);
  const proposals = getAllProposals(round);
  return [...recommenders, ...proposals];
}

export function getEvaluable(
  round: RoundAggregate,
  evaluableId: string,
): Evaluable | null {
  const node = getNode(round, evaluableId);
  if (!node) {
    return null;
  }

  if (node.type !== "recommender" && node.type !== "proposal") {
    return null;
  }

  return node;
}

export function getEvaluableOrPlaceholder(
  round: RoundAggregate,
  evaluableId: string,
): Evaluable {
  const evaluable = getEvaluable(round, evaluableId);
  if (evaluable) {
    return evaluable;
  }

  return getPlaceholderProposal(evaluableId);
}

function getPlaceholderProposal(proposalId: string): Proposal {
  return {
    id: proposalId,
    type: "proposal",
    status: "active",
    entityType: "non-profit",
    shortname: "ERR",
    lastSubmittedDatetime: new Date().toISOString(),
    firstSubmittedDatetime: new Date().toISOString(),
    requestsMatchingPledge: false,
    typedFields: {
      matchRate: 0,
      targetOutsideDonations: 1e6,
      baselineStandardGrantAmount: 1e6,
      baseRequestAmount: 1e6,
      minGrantAmount: 1e6,
      maxGrantAmount: 1e6,
      receivingOrgName: "ERR",
      receivingOrgTaxId: "ERR",
      receivingOrgLocation: "ERR",
      sffApplicationId: "ERR",
      targetsFreedom: false,
      targetsFairness: false,
      longformLink: "http://example.com",
    },
    fieldResponses: {},
    longname: "ERR",
  };
}

export function getProposalOrPlaceholder(
  round: RoundAggregate,
  proposalId: string,
): Proposal {
  const proposal = getProposal(round, proposalId);
  if (proposal) {
    return proposal;
  }

  return getPlaceholderProposal(proposalId);
}

export function getNode(
  round: RoundAggregate,
  nodeId: string,
): Node | undefined {
  const evaluator = getEvaluator(round, nodeId);
  if (evaluator) {
    return evaluator;
  }

  return getProposal(round, nodeId);
}

export function getNodeByShortname(
  round: RoundAggregate,
  shortname: string,
): Node | undefined {
  return getAllNodes(round).find((node) => node.shortname === shortname);
}

export function assertGetEvaluable(
  round: RoundAggregate,
  evaluableId: string,
): Recommender | Proposal {
  const evaluable = getEvaluable(round, evaluableId);
  if (!evaluable) {
    throw new FailedLookupById("evaluable", round, evaluableId);
  }

  return evaluable;
}

export function getProposal(
  round: RoundAggregate,
  proposalId: string,
): Proposal | undefined {
  return getAllProposals(round).find((proposal) => proposal.id === proposalId);
}

export function getProposalFormOfProposalId(
  round: RoundAggregate,
  proposalId: string,
): ProposalForm | undefined {
  return getAllProposalForms(round).find((proposalForm) =>
    Object.values(proposalForm.proposals).some(
      (proposal) => proposal.id === proposalId,
    ),
  );
}

export function assertGetProposalFormOfProposalId(
  round: RoundAggregate,
  proposalId: string,
): ProposalForm {
  const proposalForm = getProposalFormOfProposalId(round, proposalId);
  if (!proposalForm) {
    throw new FailedLookupById("proposalForm", round, proposalId);
  }
  return proposalForm;
}

export function assertGetProposal(
  proposalForm: ProposalForm,
  proposalId: string,
): Proposal {
  const proposal = proposalForm.proposals[proposalId];
  if (!proposal) {
    throw new Error(`Proposal with id ${proposalId} not found`);
  }
  return proposal;
}

export function assertGetField(proposalForm: ProposalForm, fieldId: string) {
  const field = proposalForm.fields.find((field) => field.id === fieldId);
  if (!field) {
    const fieldIds = proposalForm.fields.map((field) => field.id);
    throw new Error(
      `Field with id ${fieldId} not found. Available fields: [${fieldIds.join(", ")}]`,
    );
  }
  return field;
}

export function assertGetFunder(
  round: RoundAggregate,
  funderId: string,
): Funder {
  const evaluator = assertGetEvaluator(round, funderId);
  if (!evaluator) {
    throw new Error(`Funder not found, id: ${funderId}`);
  }

  if (evaluator.type !== "funder") {
    throw new Error(
      `Found non-funder evaluator when looking for a funder, id: ${funderId}`,
    );
  }

  return evaluator;
}

export function getAllEvaluationsForEvaluable(
  round: RoundAggregate,
  evaluableId: string,
): Evaluation[] {
  return getAllEvaluators(round).flatMap((evaluator) =>
    evaluator.evaluations[evaluableId]
      ? [evaluator.evaluations[evaluableId]]
      : [],
  );
}

export function getAllEvaluators(round: RoundAggregate): Evaluator[] {
  return Object.values(round.members).flatMap((member) =>
    Object.values(member.evaluators),
  );
}

export function getActiveEvaluationsForEvaluable(
  round: RoundAggregate,
  evaluableId: string,
): Evaluation[] {
  const evaluable = getEvaluable(round, evaluableId);
  if (evaluable?.status !== "active") {
    return [];
  }

  const evaluators =
    evaluable.type === "proposal"
      ? getActiveRecommenders(round)
      : getActiveFunders(round);

  return evaluators
    .flatMap((evaluator) => getActiveEvaluatorEvaluations(round, evaluator))
    .filter((evaluation) => evaluation.evaluableId === evaluableId);
}

// Find the evaluator ID of the evaluator who ultimately evaluates the evaluable.
export function resolveDeferredEvaluators(
  round: RoundAggregate,
  evaluator: Evaluator,
): { [evaluableId: string]: string } {
  const ultimateEvaluator: { [evaluableId: string]: string } = {};

  // The set of deferrals which haven't been resolved yet.
  let currentDeferrals = Object.fromEntries(
    Object.entries(evaluator.evaluations)
      .filter(([_, evaluation]) => evaluation.state === "deferred")
      .map(([evaluableId, evaluation]) => [
        evaluableId,
        evaluation.deferredTo!,
      ]),
  );

  while (currentDeferrals.length) {
    const newDeferrals: { [evaluableId: string]: string } = {};
    for (const [evaluableId, recommenderId] of Object.entries(
      currentDeferrals,
    )) {
      const recommender = assertGetRecommender(round, recommenderId);
      const evaluation = recommender.evaluations[evaluableId];
      if (evaluation?.state === "deferred") {
        if (evaluation.deferredTo === evaluator.id) {
          // We've returned to the original deferred-to recommender
          throw new Error(
            `Deferral cycle detected for evaluableId: ${evaluableId}`,
          );
        }
        newDeferrals[evaluableId] = evaluation.deferredTo!;
      } else {
        ultimateEvaluator[evaluableId] = recommenderId;
      }
    }

    currentDeferrals = newDeferrals;
  }

  return ultimateEvaluator;
}

// Build an MVF profile from a set of deferrals.
// Each deferral is a mapping from an evaluable ID to a recommender ID.
// It's possible for deferrals to form a chain (a defers to b, who defers to c, etc).
export function mvfProfileFromDeferrals(
  evaluator: Evaluator,
  round: RoundAggregate,
): MVFProfile {
  const ultimateEvaluator = resolveDeferredEvaluators(round, evaluator);

  const evaluablesOfRecommender: { [recommenderId: string]: Set<string> } = {};
  for (const [evaluableId, recommenderId] of Object.entries(
    ultimateEvaluator,
  )) {
    if (!(recommenderId in evaluablesOfRecommender)) {
      evaluablesOfRecommender[recommenderId] = new Set();
    }
    evaluablesOfRecommender[recommenderId].add(evaluableId);
  }

  const result: MVFProfile = {};

  for (const [recommenderId, evaluableIds] of Object.entries(
    evaluablesOfRecommender,
  )) {
    const recommender = assertGetRecommender(round, recommenderId);
    const tranches = getEvaluatorTrancheList(round, recommender);
    const mvfProfile = mvfProfileFromTrancheProfile(tranches);

    for (const evaluableId of evaluableIds) {
      result[evaluableId] = mvfProfile[evaluableId];
    }
  }

  return result;
}

// There are four cases:
// 1. The evaluator is a funder. Funders don't have deferrals, so we just return their tranche list.
// 2. The evaluator is a recommender, but they don't have any deferrals. We return their tranche list.
// 3. The evaluator is a recommender, and they have deferrals. We merge their evaluations with their deferrals.
// 4. The evaluator is a mean recommender.
export function getEvaluatorTrancheList(
  round: RoundAggregate,
  evaluator: Evaluator,
): TrancheProfile {
  if (evaluator.type !== "recommender") {
    return evaluator.trancheList;
  }

  // non-mean-recommender
  if (evaluator?.meanRecommenderThreshold == null) {
    if (
      Object.values(evaluator.evaluations).filter(
        (evaluation) => evaluation.state === "deferred",
      ).length === 0
    ) {
      return evaluator.trancheList;
    } else {
      return tranchesFromMVFProfile({
        ...mvfProfileFromTrancheProfile(evaluator.trancheList),
        ...mvfProfileFromDeferrals(evaluator, round),
      });
    }
  }

  return getMeanRecommendation(round, evaluator.meanRecommenderThreshold);
}

export function getRoundMemberForEvaluator(
  round: RoundAggregate,
  evaluatorId: string,
): RoundMember | null {
  for (const member of Object.values(round.members)) {
    if (member.evaluators[evaluatorId]) {
      return member;
    }
  }

  return null;
}

export function getRecusalForEvaluator(
  round: RoundAggregate,
  evaluatorId: string,
  evaluableId: string,
): boolean {
  return getEvaluation(round, evaluatorId, evaluableId)?.state === "recused";
}

export function assertGetMember(
  round: RoundAggregate,
  userId: string,
): RoundMember {
  const member = round.members[userId];
  if (!member) {
    throw new FailedLookupById("member", round, userId);
  }

  return member;
}

export function getAllProposalForms(round: RoundAggregate): ProposalForm[] {
  return Object.values(round.proposalForms);
}

export function getActiveProposalForms(round: RoundAggregate): ProposalForm[] {
  return Object.values(round.proposalForms).filter(isActive);
}

export function getActiveProposals(round: RoundAggregate): Proposal[] {
  return getActiveProposalForms(round)
    .flatMap((proposalForm) => Object.values(proposalForm.proposals))
    .filter(isActive);
}

export function getAllProposals(round: RoundAggregate): Proposal[] {
  return getAllProposalForms(round).flatMap((proposalForm) =>
    Object.values(proposalForm.proposals),
  );
}

function isActive(entity: { status: "active" | string }): boolean {
  return entity.status === "active";
}

function isRecommender(evaluator: Evaluator): evaluator is Recommender {
  return evaluator.type === "recommender";
}

function isFunder(evaluator: Evaluator): evaluator is Funder {
  return evaluator.type === "funder";
}

export function getAllMembers(round: RoundAggregate): RoundMember[] {
  return Object.values(round.members);
}

export function getActiveMembers(round: RoundAggregate): RoundMember[] {
  return getAllMembers(round).filter(isActive);
}

export function getAllRecommenders(round: RoundAggregate): Recommender[] {
  return getAllMembers(round)
    .flatMap((member) => Object.values(member.evaluators))
    .filter(isRecommender);
}

export type TrackRecommender = Recommender & { track: Track };

export function getActiveRecommenders(round: RoundAggregate): Recommender[] {
  return getActiveMembers(round)
    .flatMap((member) => Object.values(member.evaluators))
    .filter(isRecommender)
    .filter(isActive);
}

export function getActiveRecommendersWithTrack(
  round: RoundAggregate,
): TrackRecommender[] {
  return getActiveMembers(round).flatMap((member) =>
    Object.values(member.evaluators)
      .filter(isRecommender)
      .filter(isActive)
      .map((recommender) => ({
        ...recommender,
        track: member.track,
      })),
  );
}
export function getAllFunders(round: RoundAggregate): Funder[] {
  return getAllMembers(round)
    .flatMap((member) => Object.values(member.evaluators))
    .filter(isFunder);
}

export function getActiveFunders(round: RoundAggregate): Funder[] {
  return getActiveMembers(round)
    .flatMap((member) => Object.values(member.evaluators))
    .filter(isFunder)
    .filter(isActive);
}

export function getActiveEvaluators(round: RoundAggregate): Evaluator[] {
  return getActiveMembers(round)
    .flatMap((member) => Object.values(member.evaluators))
    .filter(isActive);
}

export function getAllNodes(
  round: RoundAggregate,
): (Recommender | Funder | Proposal)[] {
  const recommenders = getAllRecommenders(round);
  const funders = getAllFunders(round);
  const proposals = getAllProposals(round);

  return [...recommenders, ...funders, ...proposals];
}

export function assertShortnameIsUnique(
  round: RoundAggregate,
  shortname: string,
) {
  const allNodes = getAllNodes(round);

  // An anonymized shortname is not guaranteed to be unique
  // TODO: why? it seems like that should not be the case
  const matchesTrackNames = Object.values(trackNames).some((trackName) => {
    return shortname.startsWith(`${trackName} member`);
  });

  if (matchesTrackNames) {
    return;
  }

  for (const node of allNodes) {
    if (node.shortname === shortname) {
      console.error(
        `Shortname is not unique, shortname: ${shortname}, node: ${JSON.stringify(
          node,
        )}`,
      );
      throw new InvalidInputError(
        `Shortname is not unique, shortname: ${shortname}`,
      );
    }
  }
}

export function isHold(round: RoundAggregate, evaluable: Evaluable): boolean {
  return round.holdProposalId === evaluable.id;
}

export function getAllRoundMemberEvaluationsForEvaluable(
  roundMember: RoundMember,
  evaluableId: string,
): Evaluation[] {
  const evaluations: Evaluation[] = [];

  for (const evaluator of Object.values(roundMember.evaluators)) {
    const evaluation = evaluator.evaluations[evaluableId];
    if (evaluation) {
      evaluations.push(evaluation);
    }
  }

  return evaluations;
}

export function getUnevaluatedEvaluableGroups(
  round: RoundAggregate,
  evaluator: Recommender | Funder,
): {
  recused: Evaluable[];
  notStarted: Evaluable[];
  hidden: Evaluable[];
  deferred: Evaluable[];
  evaluated: Evaluable[];
  canceled: Evaluable[];
  zeroed: Evaluable[];
} {
  const evaluables =
    evaluator.type === "recommender"
      ? getActiveProposals(round)
      : getActiveRecommenders(round);

  const recused: Evaluable[] = [];
  const deferred: Evaluable[] = [];
  const hidden: Evaluable[] = [];
  const notStarted: Evaluable[] = [];
  const evaluated: Evaluable[] = [];
  const canceled: Evaluable[] = [];
  const zeroed: Evaluable[] = [];

  for (const evaluable of evaluables) {
    const evaluation = evaluator.evaluations[evaluable.id];
    if (!evaluation || evaluation.state === "not-started") {
      notStarted.push(evaluable);
    } else if (evaluation.state === "hidden") {
      hidden.push(evaluable);
    } else if (evaluation.state === "evaluated") {
      if (evaluation.setToZero) {
        zeroed.push(evaluable);
      } else {
        evaluated.push(evaluable);
      }
    } else if (evaluation.state === "canceled") {
      canceled.push(evaluable);
    } else if (evaluation.state === "deferred") {
      deferred.push(evaluable);
    } else if (evaluation.state === "recused") {
      recused.push(evaluable);
    }
  }

  return {
    recused,
    notStarted,
    hidden,
    deferred,
    evaluated,
    canceled,
    zeroed,
  };
}

export function getEvaluatorActiveEvaluationIds(
  evaluator: Evaluator,
): string[] {
  return Object.values(evaluator.evaluations)
    .filter((e) => e.state === "evaluated")
    .map((evaluation) => evaluation.evaluableId);
}

// TODO: should just return set, since it is most often (always?) used as a set
export function getActiveEvaluationIds(evaluators: Evaluator[]): string[] {
  const activeEvaluationIds = evaluators.flatMap((evaluator) =>
    getEvaluatorActiveEvaluationIds(evaluator),
  );

  return [...new Set(activeEvaluationIds)];
}

export function evaluatorIsMeanRecommender(evaluator: Evaluator): boolean {
  return (
    evaluator.type === "recommender" &&
    evaluator?.meanRecommenderThreshold != null
  );
}

export function userIsMeanRecommender(userId: string): boolean {
  return userId === meanUserId;
}

export function getActiveEvaluatorEvaluations(
  round: RoundAggregate,
  evaluator: Evaluator,
): Evaluation[] {
  if (!evaluatorIsMeanRecommender(evaluator)) {
    return Object.values(evaluator.evaluations).filter(
      (evaluation) =>
        evaluation.state === "evaluated" ||
        evaluation.evaluableId === round.holdProposalId,
    );
  }

  const recommenders = getActiveRecommenders(round);
  const evaluableIds = getActiveEvaluationIds(recommenders);

  const meanRecommenderEvaluations = evaluableIds.map((evaluableId) => {
    const evaluation: Evaluation = {
      evaluableId,
      evaluableType: "proposal",
      state: "evaluated",
    };

    return evaluation;
  });

  return meanRecommenderEvaluations;
}

export function getUserShortname(
  round: RoundAggregate,
  userId: string,
): string {
  const member = round.members[userId];
  if (!member) {
    console.error(`Failed to find member, userId: ${userId}`);
    return userId;
  }

  return member.initials;
}

export function getEvaluatorShortname(
  round: RoundAggregate,
  evaluatorId: string,
): string {
  const evaluator = getEvaluator(round, evaluatorId);
  if (!evaluator) {
    return evaluatorId;
  }

  return evaluator.shortname;
}

export function assertGetEvaluatorMemberId(
  round: RoundAggregate,
  evaluatorId: string,
): string {
  const evaluator = assertGetEvaluator(round, evaluatorId);
  return evaluator.memberId;
}

function getEvaluableMoneyField(
  round: RoundAggregate,
  evaluableId: string,
  fieldId: "minGrantAmount" | "maxGrantAmount" | "baseRequestAmount",
): number | null {
  const evaluable = Object.values(round.proposalForms)
    .flatMap((proposalForm) => Object.values(proposalForm.proposals))
    .find((proposal) => proposal.id === evaluableId);
  if (!evaluable) return null;

  return evaluable.typedFields[fieldId] ?? null;
}

export function getEvaluableRequestMin(
  round: RoundAggregate,
  evaluableId: string,
): number | null {
  return getEvaluableMoneyField(round, evaluableId, "minGrantAmount");
}

export function getEvaluableRequestMax(
  round: RoundAggregate,
  evaluableId: string,
): number | null {
  return getEvaluableMoneyField(round, evaluableId, "maxGrantAmount");
}

export function getEvaluableRequestBase(
  round: RoundAggregate,
  evaluableId: string,
): number | null {
  return getEvaluableMoneyField(round, evaluableId, "baseRequestAmount");
}

export function getProposalForm(
  round: RoundAggregate,
  proposalFormId: string,
): ProposalForm | undefined {
  return round.proposalForms[proposalFormId];
}

export function assertGetProposalForm(
  round: RoundAggregate,
  proposalFormId: string,
): ProposalForm {
  const proposalForm = round.proposalForms[proposalFormId];
  if (!proposalForm) {
    const availableIds = Object.keys(round.proposalForms);
    throw new Error(
      `Proposal form with id ${proposalFormId} not found (${availableIds.join(
        ", ",
      )})`,
    );
  }
  return proposalForm;
}

export function getProposalFormOfProposal(
  round: RoundAggregate,
  proposalId: string,
): ProposalForm | undefined {
  return Object.values(round.proposalForms).find((proposalForm) =>
    Object.values(proposalForm.proposals).some(
      (proposal) => proposal.id === proposalId,
    ),
  );
}

export function assertGetProposalFormOfProposal(
  round: RoundAggregate,
  proposalId: string,
): ProposalForm {
  const proposalForm = getProposalFormOfProposal(round, proposalId);
  if (!proposalForm) {
    throw new Error(`Proposal form not found for proposalId: ${proposalId}`);
  }
  return proposalForm;
}

export function isUserFunder(round: RoundAggregate, userId: string): boolean {
  return getActiveFunders(round).some((funder) => funder.memberId === userId);
}

export function isUserRecommenderOrFunder(
  round: RoundAggregate,
  userId: string,
): boolean {
  return getActiveEvaluators(round).some(
    (evaluator) => evaluator.memberId === userId,
  );
}

export function isUserRoundAdmin(
  round: RoundAggregate,
  userId: string,
): boolean {
  const member = round.members[userId];
  if (!member) {
    return false;
  }

  return member.isRoundAdmin;
}

export function isUserRoundAdminOrFunder(
  round: RoundAggregate,
  userId: string,
): boolean {
  return isUserRoundAdmin(round, userId) || isUserFunder(round, userId);
}

export function isUserRoundAdminOrThisEvaluator(
  round: RoundAggregate,
  evaluator: Recommender | Funder,
  userId: string,
) {
  return isUserRoundAdmin(round, userId) || evaluator.memberId === userId;
}

export function getHoldValues(round: RoundAggregate): {
  [evaluatorId: string]: number;
} {
  const recommenders = getActiveRecommenders(round);
  const holdValues: { [evaluatorId: string]: number } = {};
  const { holdProposalId } = round;
  for (const recommender of recommenders) {
    const trancheList = recommender.trancheList;
    holdValues[recommender.id] = getHoldValue(trancheList, holdProposalId);
  }
  return holdValues;
}

export function getHoldValue(
  trancheList: TrancheProfile,
  holdProposalId: string,
): number {
  const trancheBottoms = getTrancheBottoms(trancheList);
  for (let i = 0; i < trancheList.length; i++) {
    if (trancheList[i].allocations[holdProposalId]?.amount) {
      return trancheBottoms[i] + trancheList[i].height;
    }
  }
  return 0;
}

export function getTracksWithMembers(round: RoundAggregate): Track[] {
  const tracks = new Set<Track>();
  for (const member of Object.values(round.members)) {
    tracks.add(member.track);
  }
  return Array.from(tracks);
}

export function getEffectiveTrack(
  mode: AppMode,
  roundMember: RoundMember,
): Track {
  if (mode.viewAsTrackMember) {
    return mode.viewAsTrackMember;
  }

  return roundMember.track;
}

export const getTracksToShow = (
  round: RoundAggregate,
  roundMember: RoundMember | undefined,
  effectiveTrack: Track,
): Track[] => {
  let tracksToShow: Track[] = [];
  if (roundMember) {
    if (effectiveTrack === "NO_TRACK") {
      tracksToShow = getTracksWithMembers(round).filter(
        (t) => t !== "NO_TRACK",
      );
    } else if (["MAIN", "FREEDOM", "FAIRNESS"].includes(effectiveTrack)) {
      tracksToShow = [effectiveTrack];
    }
  }
  return tracksToShow;
};

export const hasTrackWithMatchingStatus = (
  round: RoundAggregate,
  userId: string,
  mode: AppMode,
  accessiblePhases: string[],
) => {
  const userRoundMember = assertGetMember(round, userId);
  const effectiveTrack = getEffectiveTrack(mode, userRoundMember);

  return getTracksToShow(round, userRoundMember, effectiveTrack).some((track) =>
    accessiblePhases.includes(round.trackSettings[track]?.status),
  );
};
